import { useCallback, useEffect, useRef, useState } from "react";
import axios from "@helpers/axios/private.axios";

const FIRST_PAGE = 1;

const mergeById = (current, next) => {
  const nextIds = new Set(next.map((item) => item.id));

  return [...current.filter((item) => !nextIds.has(item.id)), ...next];
};

/**
 * Closed trades page by page: the first page replaces the list, the next ones
 * are added to it. Starts over when the wallet or reloadKey changes.
 *
 * @param {object} params
 * @param {string} [params.walletId]
 * @param {string|number} params.reloadKey
 * @param {number} params.perPage
 * @param {(update: object[] | ((current: object[]) => object[])) => void} params.setData
 * @param {(total: number) => void} [params.onTotalChange]
 */
export const usePositionHistoryPages = ({
  walletId,
  reloadKey,
  perPage,
  setData,
  onTotalChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(FIRST_PAGE);
  const [lastPage, setLastPage] = useState(FIRST_PAGE);
  // An answer for an old wallet or an old reload is dropped
  const requestIdRef = useRef(0);

  const loadPage = useCallback(
    async (nextPage) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      setLoading(true);

      try {
        const { data } = await axios.get("/futures/positions/history", {
          params: { page: nextPage, per_page: perPage },
        });

        if (requestId !== requestIdRef.current) return;

        const items = data.data || [];

        setData((current) =>
          nextPage === FIRST_PAGE ? items : mergeById(current, items),
        );
        setPage(data.pagination.page);
        setLastPage(data.pagination.last_page);
        onTotalChange?.(data.pagination.total);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;

        // A failed next page keeps what is already shown
        if (nextPage === FIRST_PAGE) {
          setData([]);
          onTotalChange?.(0);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [onTotalChange, perPage, setData],
  );

  useEffect(() => {
    setData([]);
    setPage(FIRST_PAGE);
    setLastPage(FIRST_PAGE);
    loadPage(FIRST_PAGE);
  }, [walletId, reloadKey, loadPage, setData]);

  return {
    loading,
    canLoadMore: page < lastPage,
    loadMore: () => loadPage(page + 1),
  };
};
